import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { Metric, StatusPill } from "@/components/common";
import { api, getToken, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, Line, ComposedChart, PieChart, Pie, Cell, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  LayoutDashboard, Users, Truck, FileText, Banknote, ScrollText, ShieldCheck, Package,
  Loader2, Plus, Check, X, FileWarning, Eye, Store, Star, Pencil, Trash2
} from "lucide-react";

const PIE_COLORS = ["#2E6BFF", "#F5B301", "#34C759", "#FF5500", "#737373"];

const LOAD_STATUSES = ["Available", "Pending", "Booked", "Driver Assigned", "En Route to Pickup", "Loaded", "In Transit", "Delivered", "POD Required", "POD Uploaded", "Invoiced", "Paid", "Cancelled"];
const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "carriers", label: "Carriers", icon: Users },
  { id: "loads", label: "Loads", icon: Truck },
  { id: "quotes", label: "Quotes", icon: Package },
  { id: "bids", label: "Bids", icon: FileText },
  { id: "partners", label: "Marketplace", icon: Store },
  { id: "funding", label: "Funding Leads", icon: Banknote },
  { id: "audit", label: "Audit Log", icon: ScrollText },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const nav = TABS.map((t) => ({ to: `#${t.id}`, label: t.label, icon: t.icon, end: true }));

  return (
    <div className="min-h-screen bg-xd-bg text-white">
      <DashboardShellAdmin tab={tab} setTab={setTab}>
        {tab === "overview" && <Overview />}
        {tab === "carriers" && <Carriers />}
        {tab === "loads" && <Loads />}
        {tab === "quotes" && <Quotes />}
        {tab === "bids" && <Bids />}
        {tab === "partners" && <Partners />}
        {tab === "funding" && <FundingLeads />}
        {tab === "audit" && <AuditLog />}
      </DashboardShellAdmin>
    </div>
  );
}

// custom shell with tab-based nav
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { LogOut } from "lucide-react";

function DashboardShellAdmin({ tab, setTab, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-xd-border bg-xd-paper sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-xd-border">
          <LogoMark to="/" />
          <span className="ml-1 text-[10px] uppercase tracking-widest font-bold text-xd-gold bg-xd-gold/10 border border-xd-gold/30 rounded px-1.5 py-0.5">Admin</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${tab === t.id ? "bg-xd-orange/15 text-xd-orange" : "text-xd-muted hover:text-white hover:bg-xd-surface"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-xd-border">
          <div className="px-3 py-2 mb-2"><p className="text-sm font-semibold truncate">{user?.name}</p><p className="text-xs text-xd-dim">Administrator</p></div>
          <Button data-testid="admin-logout" variant="outline" className="w-full border-xd-border text-white hover:bg-xd-surface" onClick={() => { logout(); navigate("/"); }}><LogOut size={16} className="mr-2" /> Logout</Button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10 h-14 flex items-center px-4 gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`text-xs font-semibold whitespace-nowrap px-2 py-1 rounded ${tab === t.id ? "text-xd-orange" : "text-xd-muted"}`}>{t.label}</button>
          ))}
        </header>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">{children}</div>
      </div>
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!d) return <Spin />;
  return (
    <div>
      <h1 className="font-heading text-3xl font-black tracking-tight mb-6">Overview</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric testid="ov-carriers" label="Total Carriers" value={d.total_carriers} accent="orange" />
        <Metric testid="ov-verified" label="Verified" value={d.verified_carriers} accent="green" />
        <Metric testid="ov-pending" label="Pending Verify" value={d.pending_verifications} accent="blue" />
        <Metric testid="ov-loads" label="Total Loads" value={d.total_loads} accent="white" />
        <Metric testid="ov-booked" label="Booked Loads" value={d.booked_loads} accent="orange" />
        <Metric testid="ov-quotes" label="New Quotes" value={d.new_quotes} accent="blue" />
        <Metric testid="ov-funding" label="Funding Leads" value={d.funding_leads} accent="white" />
        <Metric testid="ov-revenue" label="Platform Revenue" value={`$${d.platform_revenue?.toLocaleString()}`} accent="green" />
      </div>

      {a && (
        <div data-testid="admin-analytics" className="mt-8 grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 bg-xd-paper border border-xd-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold">Loads & Revenue — last 6 months</h2>
              <span className="text-xs text-xd-dim">Booking rate {a.booking_rate}%</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={a.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D2D2D" vertical={false} />
                <XAxis dataKey="month" stroke="#737373" fontSize={12} />
                <YAxis yAxisId="left" stroke="#737373" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#737373" fontSize={12} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #2D2D2D", borderRadius: 8, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
                <Bar yAxisId="left" dataKey="loads" name="Loads" fill="#2E6BFF" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue $" stroke="#F5B301" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-xd-paper border border-xd-border rounded-xl p-6">
            <h2 className="font-heading text-lg font-bold mb-4">Load status mix</h2>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={a.status_breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {a.status_breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #2D2D2D", borderRadius: 8, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-1">
              {a.top_lanes.map((l, i) => (
                <div key={i} className="flex justify-between text-xs"><span className="text-xd-muted truncate">{l.lane}</span><span className="font-bold shrink-0 ml-2">{l.count}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Carriers() {
  const [list, setList] = useState(null);
  const [note, setNote] = useState("");
  const fetch = () => api.get("/admin/carriers").then((r) => setList(r.data));
  useEffect(() => { fetch(); }, []);
  if (!list) return <Spin />;

  const act = async (uid, action) => {
    try { await api.post(`/admin/carriers/${uid}/verify`, { action, note }); toast.success(`Carrier ${action}`); setNote(""); fetch(); }
    catch { toast.error("Action failed"); }
  };
  const viewDoc = (id) => window.open(`${API}/documents/${id}/download?auth=${getToken()}`, "_blank");

  return (
    <div>
      <h1 className="font-heading text-3xl font-black tracking-tight mb-6">Carriers & Verification</h1>
      <div className="space-y-4">
        {list.map((c) => (
          <div key={c.user_id} data-testid={`admin-carrier-${c.user_id}`} className="bg-xd-paper border border-xd-border rounded-xl p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold">{c.legal_name || c.user_name}</h3>
                  {c.verified ? <span className="inline-flex items-center gap-1 text-xs text-xd-green"><ShieldCheck size={13} /> Verified</span> : <StatusPill status={c.verification_status} />}
                </div>
                <p className="text-sm text-xd-muted">{c.user_email} · {c.phone}</p>
                <p className="text-xs text-xd-dim mt-1">USDOT {c.usdot || "—"} · MC {c.mc || "—"} · Authority {c.authority_status || "—"} · {c.doc_count} docs</p>
              </div>
              {!c.verified && (
                <div className="flex gap-2">
                  <Button data-testid={`approve-${c.user_id}`} size="sm" className="bg-xd-green hover:bg-xd-green/90 text-white" onClick={() => act(c.user_id, "approve")}><Check size={15} className="mr-1" /> Approve</Button>
                  <Button data-testid={`docs-${c.user_id}`} size="sm" variant="outline" className="border-xd-border text-white hover:bg-xd-surface" onClick={() => act(c.user_id, "request_docs")}><FileWarning size={15} className="mr-1" /> Docs</Button>
                  <Button data-testid={`reject-${c.user_id}`} size="sm" variant="outline" className="border-xd-red/40 text-xd-red hover:bg-xd-red/10" onClick={() => act(c.user_id, "reject")}><X size={15} className="mr-1" /> Reject</Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4"><Input data-testid="verify-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note attached to verification action…" className="bg-xd-surface border-xd-border max-w-md" /></div>
    </div>
  );
}

function Loads() {
  const [list, setList] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { origin_city: "", origin_state: "TX", dest_city: "", dest_state: "TX", pickup_date: "", trailer_type: "Gooseneck", trailer_length: 40, weight: "", commodity: "", loaded_miles: "", deadhead_miles: 0, total_pay: "", load_source: "XD Exclusive", broker_shipper: "XD FREIGHT", special_instructions: "" };
  const [form, setForm] = useState(blank);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const fetch = () => api.get("/admin/loads").then((r) => setList(r.data));
  useEffect(() => { fetch(); api.get("/admin/verified-carriers").then((r) => setCarriers(r.data)); }, []);
  if (!list) return <Spin />;

  const create = async () => {
    try {
      await api.post("/admin/loads", { ...form, weight: parseFloat(form.weight), loaded_miles: parseFloat(form.loaded_miles), deadhead_miles: parseFloat(form.deadhead_miles) || 0, total_pay: parseFloat(form.total_pay), trailer_length: parseFloat(form.trailer_length) });
      toast.success("Load created"); setOpen(false); setForm(blank); fetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed"); }
  };
  const changeStatus = async (id, status) => { try { await api.post(`/admin/loads/${id}/status`, { status }); toast.success(`Status → ${status}`); fetch(); } catch { toast.error("Error"); } };
  const assign = async (id, carrier_id) => { try { await api.post(`/admin/loads/${id}/assign`, { carrier_id }); toast.success("Carrier assigned"); fetch(); } catch { toast.error("Error"); } };
  const remove = async (id) => { try { await api.delete(`/admin/loads/${id}`); toast.success("Deleted"); fetch(); } catch { toast.error("Error"); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Loads</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="admin-create-load" className="bg-xd-orange hover:bg-xd-orangeH text-white"><Plus size={16} className="mr-2" /> Create Load</Button></DialogTrigger>
          <DialogContent className="bg-xd-paper border-xd-border text-white max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Load</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <FF label="Origin city" testid="cl-oc" value={form.origin_city} onChange={set("origin_city")} />
              <FF label="Origin state" testid="cl-os" value={form.origin_state} onChange={set("origin_state")} />
              <FF label="Dest city" testid="cl-dc" value={form.dest_city} onChange={set("dest_city")} />
              <FF label="Dest state" testid="cl-ds" value={form.dest_state} onChange={set("dest_state")} />
              <FF label="Pickup date" type="date" testid="cl-pickup" value={form.pickup_date} onChange={set("pickup_date")} />
              <div><Label className="text-xd-muted text-xs">Trailer</Label>
                <Select value={form.trailer_type} onValueChange={(v) => setForm({ ...form, trailer_type: v })}>
                  <SelectTrigger data-testid="cl-trailer" className="mt-1 bg-xd-surface border-xd-border"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Gooseneck", "Bumper Pull", "Flatbed"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <FF label="Trailer length" type="number" testid="cl-len" value={form.trailer_length} onChange={set("trailer_length")} />
              <FF label="Weight (lbs)" type="number" testid="cl-weight" value={form.weight} onChange={set("weight")} />
              <FF label="Commodity" testid="cl-commodity" value={form.commodity} onChange={set("commodity")} />
              <FF label="Loaded miles" type="number" testid="cl-loaded" value={form.loaded_miles} onChange={set("loaded_miles")} />
              <FF label="Deadhead miles" type="number" testid="cl-dh" value={form.deadhead_miles} onChange={set("deadhead_miles")} />
              <FF label="Total pay ($)" type="number" testid="cl-pay" value={form.total_pay} onChange={set("total_pay")} />
            </div>
            <DialogFooter><Button data-testid="cl-submit" onClick={create} className="bg-xd-orange hover:bg-xd-orangeH text-white">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto rounded-lg border border-xd-border">
        <table className="w-full text-sm">
          <thead className="bg-xd-surface text-xd-dim text-xs uppercase tracking-wide">
            <tr>{["XD #", "Lane", "Pay", "Carrier", "Status", "Assign", ""].map((h) => <th key={h} className="text-left font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id} data-testid={`admin-load-${l.xd_number}`} className="border-t border-xd-border hover:bg-xd-surface">
                <td className="px-4 py-3 font-mono text-xs text-xd-dim">{l.xd_number}</td>
                <td className="px-4 py-3 whitespace-nowrap font-medium">{l.origin_city} → {l.dest_city}</td>
                <td className="px-4 py-3 font-bold">${l.total_pay?.toLocaleString()}</td>
                <td className="px-4 py-3 text-xs">{l.carrier_name || "—"}</td>
                <td className="px-4 py-3">
                  <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                    <SelectTrigger data-testid={`status-${l.xd_number}`} className="h-8 w-40 bg-xd-surface border-xd-border text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{LOAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <Select onValueChange={(v) => assign(l.id, v)}>
                    <SelectTrigger data-testid={`assign-${l.xd_number}`} className="h-8 w-36 bg-xd-surface border-xd-border text-xs"><SelectValue placeholder="Assign…" /></SelectTrigger>
                    <SelectContent>{carriers.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3"><Button data-testid={`del-load-${l.xd_number}`} size="sm" variant="ghost" className="text-xd-red hover:bg-xd-red/10" onClick={() => remove(l.id)}><X size={15} /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Quotes() {
  const [list, setList] = useState(null);
  const [resp, setResp] = useState({});
  const fetch = () => api.get("/admin/quotes").then((r) => setList(r.data));
  useEffect(() => { fetch(); }, []);
  if (!list) return <Spin />;
  const respond = async (id) => { try { await api.post(`/admin/quotes/${id}/respond`, { status: "responded", response: resp[id] || "" }); toast.success("Response sent"); fetch(); } catch { toast.error("Error"); } };

  return (
    <div>
      <h1 className="font-heading text-3xl font-black tracking-tight mb-6">Shipper Quote Requests</h1>
      {list.length === 0 ? <p className="text-xd-muted">No quotes yet.</p> : (
        <div className="space-y-4">
          {list.map((q) => (
            <div key={q.id} data-testid={`admin-quote-${q.xd_ref}`} className="bg-xd-paper border border-xd-border rounded-xl p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs text-xd-dim">{q.xd_ref}</span><StatusPill status={q.status} /></div>
                  <h3 className="mt-1 font-heading font-bold">{q.origin} → {q.destination}</h3>
                  <p className="text-sm text-xd-muted">{q.company} · {q.contact} · {q.email} · {q.phone}</p>
                  <p className="text-sm text-xd-muted">{q.commodity} · {q.weight ? `${q.weight.toLocaleString()} lbs` : "—"} · {q.trailer_type || "Any"}</p>
                  {q.special_instructions && <p className="text-xs text-xd-dim mt-1">Notes: {q.special_instructions}</p>}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Input data-testid={`quote-resp-${q.xd_ref}`} value={resp[q.id] || q.admin_response || ""} onChange={(e) => setResp({ ...resp, [q.id]: e.target.value })} placeholder="Type your response to the shipper…" className="bg-xd-surface border-xd-border" />
                <Button data-testid={`quote-send-${q.xd_ref}`} onClick={() => respond(q.id)} className="bg-xd-orange hover:bg-xd-orangeH text-white shrink-0">Send</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Bids() {
  const [list, setList] = useState(null);
  const [counter, setCounter] = useState({});
  const fetch = () => api.get("/admin/bids").then((r) => setList(r.data));
  useEffect(() => { fetch(); }, []);
  if (!list) return <Spin />;

  const setStatus = async (id, status, counter_rate) => {
    try { await api.post(`/admin/bids/${id}/status`, { status, counter_rate: counter_rate ? parseFloat(counter_rate) : null }); toast.success(`Bid ${status}`); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const badge = { pending: "text-xd-yellow", countered: "text-xd-blue", won: "text-xd-green", lost: "text-xd-dim" };

  return (
    <div>
      <h1 className="font-heading text-3xl font-black tracking-tight mb-6">Carrier Bids</h1>
      {list.length === 0 ? <p className="text-xd-muted">No bids yet.</p> : (
        <div className="overflow-x-auto rounded-lg border border-xd-border">
          <table className="w-full text-sm">
            <thead className="bg-xd-surface text-xd-dim text-xs uppercase"><tr>{["XD #", "Carrier", "Bid", "Status", "Counter $", "Actions"].map((h) => <th key={h} className="text-left font-bold px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>{list.map((b) => (
              <tr key={b.id} data-testid={`admin-bid-${b.id}`} className="border-t border-xd-border hover:bg-xd-surface">
                <td className="px-4 py-3 font-mono text-xs text-xd-dim">{b.xd_number}</td>
                <td className="px-4 py-3">{b.carrier_name}</td>
                <td className="px-4 py-3 font-bold">${b.rate?.toLocaleString()}</td>
                <td className={`px-4 py-3 font-bold uppercase text-xs ${badge[b.status] || "text-xd-muted"}`}>{b.status || "pending"}</td>
                <td className="px-4 py-3">
                  <Input data-testid={`bid-counter-${b.id}`} value={counter[b.id] ?? (b.counter_rate || "")} onChange={(e) => setCounter({ ...counter, [b.id]: e.target.value })} placeholder="$" className="h-8 w-24 bg-xd-surface border-xd-border text-xs" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <Button data-testid={`bid-counter-btn-${b.id}`} size="sm" variant="outline" className="border-xd-blue/40 text-xd-blue hover:bg-xd-blue/10 h-8" onClick={() => setStatus(b.id, "countered", counter[b.id])}>Counter</Button>
                    <Button data-testid={`bid-win-btn-${b.id}`} size="sm" className="bg-xd-green hover:bg-xd-green/90 text-white h-8" onClick={() => setStatus(b.id, "won")}>Win</Button>
                    <Button data-testid={`bid-lose-btn-${b.id}`} size="sm" variant="outline" className="border-xd-red/40 text-xd-red hover:bg-xd-red/10 h-8" onClick={() => setStatus(b.id, "lost")}>Decline</Button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FundingLeads() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/funding-leads").then((r) => setList(r.data)); }, []);
  if (!list) return <Spin />;
  return (
    <div>
      <h1 className="font-heading text-3xl font-black tracking-tight mb-6">Funding Leads</h1>
      {list.length === 0 ? <p className="text-xd-muted">No leads yet.</p> : (
        <div className="space-y-3">{list.map((l) => (
          <div key={l.id} className="bg-xd-paper border border-xd-border rounded-lg p-4 flex justify-between flex-wrap gap-2">
            <div><p className="font-semibold">{l.product}</p><p className="text-sm text-xd-muted">{l.name} · {l.email} · {l.phone}</p>{l.notes && <p className="text-xs text-xd-dim">{l.notes}</p>}</div>
            <div className="text-right"><p className="text-sm font-bold">{l.amount || "—"}</p><p className="text-xs text-xd-dim">{new Date(l.created_at).toLocaleDateString()}</p></div>
          </div>
        ))}</div>
      )}
    </div>
  );
}

function AuditLog() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/audit-log").then((r) => setList(r.data)); }, []);
  if (!list) return <Spin />;
  return (
    <div>
      <h1 className="font-heading text-3xl font-black tracking-tight mb-6">Audit Log</h1>
      <div className="space-y-2">
        {list.map((a) => (
          <div key={a.id} data-testid={`audit-${a.id}`} className="flex items-start gap-3 bg-xd-paper border border-xd-border rounded-lg p-3 text-sm">
            <ScrollText size={16} className="text-xd-orange mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p><span className="font-semibold text-white">{a.action}</span> <span className="text-xd-muted">on {a.entity_type}</span></p>
              <p className="text-xs text-xd-dim">{a.actor_name} ({a.actor_role}) · {new Date(a.created_at).toLocaleString()}{a.meta && Object.keys(a.meta).length ? ` · ${JSON.stringify(a.meta)}` : ""}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Partners() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { company: "", category: "", description: "", offer: "", eligibility: "", website: "", cta_text: "Learn More", who_for: "", services: "", disclosure: "", featured: false };
  const [form, setForm] = useState(blank);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const fetch = () => api.get("/admin/partners").then((r) => setList(r.data));
  useEffect(() => { fetch(); }, []);
  if (!list) return <Spin />;

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...blank, ...p, services: Array.isArray(p.services) ? p.services.join(", ") : (p.services || "") });
    setOpen(true);
  };
  const save = async () => {
    const payload = { ...form, services: form.services ? form.services.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    try {
      if (editing) await api.put(`/admin/partners/${editing}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success(editing ? "Partner updated" : "Partner added"); setOpen(false); setForm(blank); setEditing(null); fetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const toggleFeature = async (p) => { try { await api.post(`/admin/partners/${p.id}/feature`, { featured: !p.featured }); fetch(); } catch { toast.error("Error"); } };
  const remove = async (id) => { try { await api.delete(`/admin/partners/${id}`); toast.success("Removed"); fetch(); } catch { toast.error("Error"); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Marketplace Partners</h1>
        <Button data-testid="admin-add-partner" onClick={openNew} className="bg-xd-orange hover:bg-xd-orangeH text-white"><Plus size={16} className="mr-2" /> Add Partner</Button>
      </div>

      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} data-testid={`admin-partner-${p.id}`} className="bg-xd-paper border border-xd-border rounded-xl p-5 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-bold">{p.company}</h3>
                {p.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-xd-gold bg-xd-gold/10 border border-xd-gold/30 rounded px-1.5 py-0.5"><Star size={11} /> Featured</span>}
              </div>
              <p className="text-xs text-xd-dim mt-0.5">{p.category}</p>
              {p.description && <p className="text-sm text-xd-muted mt-1 line-clamp-2">{p.description}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button data-testid={`feature-partner-${p.id}`} size="sm" variant="outline" className={`border-xd-border hover:bg-xd-surface ${p.featured ? "text-xd-gold" : "text-white"}`} onClick={() => toggleFeature(p)}><Star size={15} /></Button>
              <Button data-testid={`edit-partner-${p.id}`} size="sm" variant="outline" className="border-xd-border text-white hover:bg-xd-surface" onClick={() => openEdit(p)}><Pencil size={15} /></Button>
              <Button data-testid={`del-partner-${p.id}`} size="sm" variant="ghost" className="text-xd-red hover:bg-xd-red/10" onClick={() => remove(p.id)}><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-xd-muted">No partners yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-xd-paper border-xd-border text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Partner" : "Add Partner"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Company" testid="pt-company" value={form.company} onChange={set("company")} />
            <FF label="Category" testid="pt-category" value={form.category} onChange={set("category")} />
            <div className="col-span-2"><Label className="text-xd-muted text-xs">Description</Label><Textarea data-testid="pt-description" value={form.description} onChange={set("description")} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <FF label="Offer" testid="pt-offer" value={form.offer} onChange={set("offer")} />
            <FF label="Eligibility" testid="pt-eligibility" value={form.eligibility} onChange={set("eligibility")} />
            <FF label="Who it's for" testid="pt-whofor" value={form.who_for} onChange={set("who_for")} />
            <FF label="Website URL" testid="pt-website" value={form.website} onChange={set("website")} />
            <FF label="CTA text" testid="pt-cta" value={form.cta_text} onChange={set("cta_text")} />
            <div className="col-span-2"><Label className="text-xd-muted text-xs">Services (comma-separated)</Label><Input data-testid="pt-services" value={form.services} onChange={set("services")} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div className="col-span-2"><Label className="text-xd-muted text-xs">Disclosure</Label><Textarea data-testid="pt-disclosure" value={form.disclosure} onChange={set("disclosure")} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm text-xd-muted cursor-pointer">
              <input data-testid="pt-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-xd-gold w-4 h-4" />
              Feature this partner on the marketplace
            </label>
          </div>
          <DialogFooter><Button data-testid="pt-submit" onClick={save} disabled={!form.company || !form.category} className="bg-xd-orange hover:bg-xd-orangeH text-white">{editing ? "Save Changes" : "Add Partner"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Spin() { return <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div>; }
function FF({ label, testid, ...props }) {
  return <div><Label className="text-xd-muted text-xs">{label}</Label><Input data-testid={testid} {...props} value={props.value ?? ""} className="mt-1 bg-xd-surface border-xd-border" /></div>;
}
